/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  type ReactNode,
} from "react";
import { onAuthStateChanged } from "firebase/auth";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "../services/firebase/firebase.config";
import type { AuthUser } from "../services/firebase/auth.service";
import { markAuthStart, markAuthComplete } from "../utils/performance";
import {
  getWeeklyCoaching,
  generateAndCacheWeeklyCoaching,
  shouldRunWeeklyEvaluator,
} from "../services/weeklyCoaching.service";
import { getUserProfile, getUserProfileResult, saveUserProfile, markStarterModalSeen } from "../services/userProfile.service";
import type { DoseDay, StarterModalScreen } from "../services/userProfile.service";
import type { WeeklyCoachingResult } from "../services/behaviourEngine.service";
import { useDailySummary, type DailySummaryContextValue } from "../hooks/useDailySummary";
import { getSubscription } from "../services/stripe.service";
import { activatePreviewAfterVerification } from "../services/premiumPreview.service";

import { initDemoState } from "../demo/demoState";

const auth = getAuth(firebaseApp);

export type OnboardingStatus = 'loading' | 'incomplete' | 'complete' | 'error';

export type AppEntitlementReason = 'premium' | 'preview' | 'admin' | 'none' | 'no_record';

interface AppContextValue {
  user: AuthUser | null;
  loading: boolean;
  isDemoMode: boolean;
  authError: Error | null;
  userCreationTime?: string | number | null;
  weeklyCoaching: WeeklyCoachingResult | null;
  weeklyCoachingLoading: boolean;
  glp1Enabled: boolean;
  glp1DoseDay: DoseDay | null;
  glp1CheckComplete: boolean;
  setGlp1Enabled: (enabled: boolean) => void;
  setGlp1DoseDay: (day: DoseDay | null) => void;
  setGlp1EnabledAndPersist: (enabled: boolean) => Promise<void>;
  setGlp1DoseDayAndPersist: (day: DoseDay | null) => Promise<void>;
  firstName: string | null;
  // Profile-based flags (Bug 1 + Bug 2 fix)
  // profileSettled: true after profile fetch finishes (success or error).
  // MUST check profileError before trusting onboardingComplete or isAdmin.
  profileSettled: boolean;
  profileError: Error | null;
  onboardingComplete: boolean;
  onboardingStatus: OnboardingStatus;
  isAdmin: boolean;
  /**
   * Consolidated readiness flag for submit-capable surfaces.
   *
   * True when every prerequisite for a write has settled:
   *   - auth resolved (loading === false)
   *   - user exists (user !== null)
   *   - no auth error
   *   - profile fetch settled (profileSettled === true)
   *   - no profile error
   *
   * Demo mode is always ready.
   *
   * Screens that can trigger writes (nutrition logging, save, photo upload, etc.)
   * MUST gate their submit UI on this flag. Until it is true:
   *   - submit buttons must be disabled with visible "not ready" affordance
   *   - any submit handler that runs defensively must surface UI feedback on !ready
   */
  isAppReady: boolean;
  retryProfile: () => void;
  dailySummary: DailySummaryContextValue;
  // Feature gating
  isPremium: boolean;
  plan: 'basic' | 'trial' | 'premium';
  subscriptionLoading: boolean;
  entitlementReason: AppEntitlementReason | null;
  // Premium preview
  premiumPreviewExpiresAt: string | null;
  premiumPreviewUsed: boolean;
  previewActive: boolean;
  // Durable email verification state
  emailVerificationCompletedAt: number | null;
  // Functional Age first-run gate
  functionalAgeHasEverComputed: boolean;
  refetchSubscription: () => Promise<void>;
  refreshAuthUser: () => Promise<void>;
  applyEntitlement: (payload: import('../services/premiumPreview.service').ActivateAfterVerificationResponse) => void;
  // ── Starter modal central store ──────────────────────────────────────────────
  // starterModalsLoaded: true once the seen-map has been read from Firestore.
  // Screens must not decide whether to show a modal until this is true.
  starterModalsLoaded: boolean;
  // starterModalsSeen: in-memory record of which screens have been seen this session.
  // Populated from profile on load. Updated optimistically on dismiss.
  starterModalsSeen: Partial<Record<StarterModalScreen, boolean>>;
  // markStarterSeen: updates in-memory state immediately, then persists to Firestore
  // in the background. Never waits for Firestore before hiding the modal.
  markStarterSeen: (screen: StarterModalScreen) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [userCreationTime, setUserCreationTime] = useState<
    string | number | null
  >(null);

  // P0 FIX: Track whether initial auth check has completed
  const initialAuthResolved = useRef(false);
  // P0 FIX: Track current user UID to detect actual user changes vs token refreshes
  const currentUidRef = useRef<string | null>(null);

  // Demo mode — single canonical bootstrap, no polling, no split-brain
  const isDemoMode = initDemoState().isDemo;
  useEffect(() => {
    let isMounted = true;
    let authTimeout: ReturnType<typeof setTimeout> | null = null;

    markAuthStart();

    authTimeout = setTimeout(() => {
      if (isMounted) {
        console.warn(
          "[AppContext] Auth state check timeout - stopping loading state",
        );
        initialAuthResolved.current = true;
        setLoading(false);
        markAuthComplete();
      }
    }, 5000);

    const unsubscribe = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        if (!isMounted) return;

        if (authTimeout) {
          clearTimeout(authTimeout);
          authTimeout = null;
        }

        try {
          if (firebaseUser) {
            // P0 FIX: If initial auth already resolved and this is the SAME user,
            // this is just a token refresh — update user state but DON'T touch loading.
            const isSameUser = currentUidRef.current === firebaseUser.uid;
            const isSubsequentFire = initialAuthResolved.current && isSameUser;

            if (isSubsequentFire) {
              console.log("[AppContext] P0 FIX: Subsequent auth fire for same user (token refresh) — skipping loading toggle");
              setUser(prev => ({
                uid: firebaseUser.uid,
                email: firebaseUser.email,
                displayName: firebaseUser.displayName,
                emailVerified: prev?.emailVerified === true ? true : firebaseUser.emailVerified,
              }));
              setUserCreationTime(firebaseUser.metadata.creationTime || null);
              setAuthError(null);
              return;
            }

            try {
              let token: string | null = null;
              try {
                token = await firebaseUser.getIdToken(false);
              } catch {
                token = await firebaseUser.getIdToken(true);
              }

              if (token) {
                currentUidRef.current = firebaseUser.uid;

                setUser(prev => ({
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  displayName: firebaseUser.displayName,
                  emailVerified: prev?.emailVerified === true ? true : firebaseUser.emailVerified,
                }));
                setUserCreationTime(firebaseUser.metadata.creationTime || null);
                setAuthError(null);
              }
            } catch (tokenError) {
              console.warn(
                "[AppContext] Token refresh failed, auto-logging out:",
                tokenError,
              );

              try {
                const { signOutUser } =
                  await import("../services/firebase/auth.service");
                await signOutUser();
                console.log(
                  "[AppContext] Successfully signed out user after token failure",
                );
              } catch (signOutError) {
                console.error(
                  "[AppContext] Failed to sign out user:",
                  signOutError,
                );
              }

              currentUidRef.current = null;
              setUser(null);
              setUserCreationTime(null);
              setAuthError(new Error("Session expired. Please sign in again."));
            }
          } else {
            currentUidRef.current = null;
            setUser(null);
            setUserCreationTime(null);
            setAuthError(null);

          }
        } catch (error) {
          console.error("Auth state change error:", error);
          if (isMounted) {
            currentUidRef.current = null;
            setUser(null);
            setAuthError(
              error instanceof Error
                ? error
                : new Error("Authentication error"),
            );
          }
        } finally {
          if (isMounted) {
            initialAuthResolved.current = true;
            setLoading(false);
            markAuthComplete();
          }
        }
      },
      (error) => {
        console.error("[AppContext] onAuthStateChanged error:", error);

        if (authTimeout) {
          clearTimeout(authTimeout);
          authTimeout = null;
        }

        if (isMounted) {
          const errorCode = (error as { code?: string })?.code;
          const isTokenError =
            errorCode === "auth/user-token-expired" ||
            errorCode === "auth/invalid-user-token" ||
            errorCode === "auth/user-disabled" ||
            error.message?.includes("token") ||
            error.message?.includes("expired");

          if (isTokenError) {
            console.warn(
              "[AppContext] Token-related auth error detected, ensuring user is signed out",
            );
            import("../services/firebase/auth.service")
              .then(({ signOutUser }) => {
                signOutUser().catch((signOutError) => {
                  console.error(
                    "[AppContext] Failed to sign out after auth error:",
                    signOutError,
                  );
                });
              })
              .catch((importError) => {
                console.error(
                  "[AppContext] Failed to import signOutUser:",
                  importError,
                );
              });
          }

          currentUidRef.current = null;
          setUser(null);
          setUserCreationTime(null);
          setAuthError(
            error instanceof Error
              ? error
              : new Error("Authentication error. Please sign in again."),
          );
          initialAuthResolved.current = true;
          setLoading(false);
          markAuthComplete();
        }
      },
    );

    return () => {
      isMounted = false;
      if (authTimeout) {
        clearTimeout(authTimeout);
      }
      unsubscribe();
    };
  }, []);

  // ITEM 4: In demo mode, skip auth check
  useEffect(() => {
    if (isDemoMode) {
      console.log(
        "[AppContext] [ITEM 4] Demo mode active - skipping auth check and setting loading to false",
      );
      setLoading(false);
    }
  }, [isDemoMode]);

  // ================================================================
  // SINGLE PROFILE FETCH — source of truth for onboarding, admin, GLP-1
  // Replaces the two separate getUserProfile calls that previously existed.
  // ================================================================
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [profileError, setProfileError] = useState<Error | null>(null);
  const [onboardingComplete, setOnboardingComplete] = useState(() => {
    // Seed from localStorage cache so returning users never flash to onboarding
    // while the Firestore profile fetch is in flight. Firestore confirms/corrects on load.
    try {
      const uid = auth.currentUser?.uid;
      if (uid) return localStorage.getItem(`coretrak.onboarding.${uid}`) === 'complete';
    } catch { /* ignore */ }
    return false;
  });
  const [isAdmin, setIsAdmin] = useState(false);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [glp1Enabled, setGlp1Enabled] = useState(false);
  const [glp1DoseDay, setGlp1DoseDay] = useState<DoseDay | null>(null);
  const [glp1CheckComplete, setGlp1CheckComplete] = useState(false);

  // ── Starter modal central store ──────────────────────────────────────────────
  const [starterModalsLoaded, setStarterModalsLoaded] = useState(false);
  const [starterModalsSeen, setStarterModalsSeen] = useState<Partial<Record<StarterModalScreen, boolean>>>({});

  // markStarterSeen: update in-memory state immediately, persist to Firestore in background.
  // Never waits for Firestore — modal hides instantly on dismiss.
  // Uses markStarterModalSeen which writes via dot-notation so only this screen's
  // flag is updated — other screen flags in the map are never overwritten.
  const markStarterSeen = useCallback((screen: StarterModalScreen) => {
    setStarterModalsSeen(prev => ({ ...prev, [screen]: true }));
    const uid = auth.currentUser?.uid;
    if (uid) {
      markStarterModalSeen(uid, screen);
    }
  }, []);

  // Retry counter — bump to re-trigger profile fetch
  const [profileRetryCount, setProfileRetryCount] = useState(0);

  // Feature gating — reads from Stripe via getSubscription
  // Demo mode always gets premium so the demo shows full features
  const [plan, setPlan] = useState<'basic' | 'trial' | 'premium'>('basic');
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [premiumPreviewExpiresAt, setPremiumPreviewExpiresAt] = useState<string | null>(null);
  const [premiumPreviewUsed, setPremiumPreviewUsed] = useState(false);
  const [previewActive, setPreviewActive] = useState(false);
  const [emailVerificationCompletedAt, setEmailVerificationCompletedAt] = useState<number | null>(null);
  const [functionalAgeHasEverComputed, setFunctionalAgeHasEverComputed] = useState(false);
  const [entitlementReason, setEntitlementReason] = useState<AppEntitlementReason | null>(null);

  const retryProfile = useCallback(() => {
    console.log("[AppContext] Profile fetch retry requested");
    setProfileLoaded(false);
    setProfileError(null);
    setProfileRetryCount((c) => c + 1);
  }, []);

  // ── Subscription fetch — runs once after auth settles ──
  useEffect(() => {
    if (isDemoMode) {
      setPlan('premium');
      setSubscriptionLoading(false);
      return;
    }
    if (!user) {
      setPlan('basic');
      setSubscriptionLoading(false);
      return;
    }
    let mounted = true;
    setSubscriptionLoading(true);
    getSubscription()
      .then(async (res) => {
        if (!mounted) return;
        const p = res.data?.plan;
        if (p === 'premium' || p === 'trial') setPlan(p);
        else setPlan('basic');
        // Preview state and entitlement reason — set from server response
        setPremiumPreviewExpiresAt(res.data?.premiumPreviewExpiresAt ?? null);
        setPremiumPreviewUsed(res.data?.premiumPreviewUsed ?? false);
        setPreviewActive(res.data?.previewActive ?? false);
        setEntitlementReason((res.data?.entitlementReason as AppEntitlementReason) ?? 'none');

      })
      .catch(() => {
        if (!mounted) return;
        setPlan('basic'); // fail safe — never lock paying users out on error
        setEntitlementReason('none');
      })
      .finally(() => {
        if (mounted) setSubscriptionLoading(false);
      });
    return () => { mounted = false; };
  }, [user?.uid, user?.emailVerified, isDemoMode]);

  // Single-writer persist wrappers: update local state + Firestore + localStorage cache
  const setGlp1EnabledAndPersist = useCallback(async (enabled: boolean) => {
    setGlp1Enabled(enabled);
    if (user) {
      await saveUserProfile(user.uid, { glp1Enabled: enabled });
      try { localStorage.setItem("coretrak.glp1.enabled", String(enabled)); } catch { /* ignore */ }
    }
  }, [user]);

  const setGlp1DoseDayAndPersist = useCallback(async (day: DoseDay | null) => {
    setGlp1DoseDay(day);
    if (user) {
      await saveUserProfile(user.uid, { glp1DoseDay: day });
      try { localStorage.setItem("coretrak.glp1.doseDay", day || ""); } catch { /* ignore */ }
    }
  }, [user]);

  useEffect(() => {
    if (isDemoMode) {
      setGlp1Enabled(true);
      setGlp1DoseDay("mon");
      setGlp1CheckComplete(true);
      setProfileLoaded(true);
      setProfileError(null);
      setOnboardingComplete(true);
      setIsAdmin(false);
      // Demo mode: treat all starter modals as already seen — never show in demo
      setStarterModalsSeen({
        home: true,
        nutrition: true,
        exercise: true,
        body_stats: true,
        functional_age: true,
        glp1_hub: true,
        a2hs: true,
      });
      setStarterModalsLoaded(true);
      return;
    }

    if (!user) {
      // No user yet (either logged out or auth still resolving).
      // Reset GLP-1 defaults but do NOT mark profile as settled —
      // if auth is still loading, we need to wait for the user to be set
      // before we can fetch the profile and determine onboarding status.
      setGlp1Enabled(false);
      setGlp1DoseDay(null);
      setGlp1CheckComplete(true);
      // Only mark profile as settled if auth has fully resolved (loading=false).
      // This prevents the race where profileSettled=true + onboardingComplete=false
      // causes a redirect to /onboarding before the real profile fetch runs.
      if (!loading) {
        setProfileLoaded(true);
        setProfileError(null);
        setOnboardingComplete(false);
        setIsAdmin(false);
        setStarterModalsSeen({});
        setStarterModalsLoaded(true);
      }
      return;
    }

    let mounted = true;

    // P0 FIX: Reset ALL profile gate flags to "unknown" before async fetch.
    // Without this, stale state from a prior logout (profileLoaded=true,
    // onboardingComplete=false) persists for one render cycle and causes
    // ProtectedRoute to see "settled + incomplete" → misroute to /onboarding.
    // Reset everything derived from profile — new user identity = clean slate.
    setProfileLoaded(false);
    setProfileError(null);
    setOnboardingComplete(false);
    setIsAdmin(false);
    // Reset starter modal store on user change — fresh read incoming
    setStarterModalsLoaded(false);
    setStarterModalsSeen({});

    async function loadProfile() {
      const result = await getUserProfileResult(user!.uid);

      if (!mounted) return;

      if (result.status === 'found') {
        const profile = result.profile;

        // Onboarding — read exactly the field OnboardingScreen writes
        const isComplete = profile.onboarding_complete === true;
        setOnboardingComplete(isComplete);

        // Sync localStorage cache for speed on next cold start
        try {
          const key = `coretrak.onboarding.${user!.uid}`;
          if (isComplete) localStorage.setItem(key, "complete");
          else localStorage.removeItem(key);
        } catch { /* ignore */ }

        // Admin — only set from confirmed profile data
        setIsAdmin(profile.isAdmin === true);

        // firstName
        setFirstName(profile.firstName ?? null);

        // Durable email verification
        setEmailVerificationCompletedAt(profile.emailVerificationCompletedAt ?? null);

        // Functional Age first-run gate
        setFunctionalAgeHasEverComputed(profile.functionalAgeHasEverComputed === true);

        // GLP-1
        setGlp1Enabled(profile.glp1Enabled === true);
        setGlp1DoseDay(profile.glp1DoseDay ?? null);
        setGlp1CheckComplete(true);

        // ── Starter modal seen-map — loaded once, cached in memory for session ──
        // Safe default: empty object (show nothing) until this is set.
        setStarterModalsSeen(profile.starter_modals_seen ?? {});
        setStarterModalsLoaded(true);

        setProfileError(null);
        setProfileLoaded(true);

        // Passive once-per-session reconciliation — backend decides whether to activate
        // Uses emailVerificationCompletedAt as source of truth — independent of Firebase auth state
        if (
          profile.premiumPreviewPending === true &&
          profile.emailVerificationCompletedAt != null &&
          !hasAttemptedReconciliationRef.current &&
          !previewActive
        ) {
          hasAttemptedReconciliationRef.current = true;
          activatePreviewAfterVerification()
            .then((result) => {
              console.log('[PREVIEW_RECONCILE] backend result:', result.status);
              if (result.status === 'activated' || result.status === 'already_active') {
                applyEntitlement(result);
              }
            })
            .catch((err) => {
              console.warn('[PREVIEW_RECONCILE] failed (non-fatal):', err);
            });
        }

        console.log("[AppContext] Profile loaded:", {
          userId: user!.uid,
          onboarding_complete: isComplete,
          isAdmin: profile.isAdmin === true,
          glp1Enabled: profile.glp1Enabled === true,
          starterModalsSeen: profile.starter_modals_seen,
        });
        return;
      }

      if (result.status === 'missing') {
        // No profile doc exists yet — brand-new user.
        // Set safe defaults for profile-backed state. Do NOT set isAdmin — it
        // defaults to false from useState and must only be set from confirmed
        // profile data. Do NOT set profileError — missing profile is not an error.
        console.log("[AppContext] No profile doc found — new user, continuing with safe defaults.");
        setOnboardingComplete(false);
        setFirstName(null);
        setGlp1Enabled(false);
        setGlp1DoseDay(null);
        setGlp1CheckComplete(true);
        setProfileError(null);
        setFunctionalAgeHasEverComputed(false);
        setEmailVerificationCompletedAt(null);
        // New user — no starter modals seen yet
        setStarterModalsSeen({});
        setStarterModalsLoaded(true);
        setProfileLoaded(true);
        return;
      }

      // status === 'error' — genuine fetch failure.
      // Do NOT set isAdmin false — leave it at its current state so a prior
      // successful load or server-side bypass is not clobbered.
      // ProtectedRoute guards the fatal screen with !isAdmin && !isPremium so
      // admin/bypass users are never blocked even if profile errors.
      console.error("[AppContext] Profile fetch failed:", result.error);
      setProfileError(
        result.error instanceof Error ? result.error : new Error("Failed to load profile"),
      );
      setProfileLoaded(true);
      setGlp1Enabled(false);
      setGlp1DoseDay(null);
      setGlp1CheckComplete(true);
      // On error, safe default is to not show any starter modals
      setStarterModalsSeen({});
      setStarterModalsLoaded(true);
    }

    loadProfile();

    return () => {
      mounted = false;
    };
  }, [user?.uid, loading, isDemoMode, profileRetryCount]);

  // Listen for onboarding-complete event (fired by OnboardingScreen after save)
  useEffect(() => {
    function handleOnboardingComplete(event: CustomEvent<{ userId: string }>) {
      if (event.detail.userId === user?.uid) {
        console.log("[AppContext] Received onboarding-complete event");
        setOnboardingComplete(true);
      }
    }
    window.addEventListener("onboarding-complete", handleOnboardingComplete as EventListener);
    return () => window.removeEventListener("onboarding-complete", handleOnboardingComplete as EventListener);
  }, [user?.uid]);

  // Listen for email-verification-completed event — updates durable state and triggers reconciliation
  useEffect(() => {
    async function handleEmailVerificationCompleted(evt: Event) {
      const event = evt as CustomEvent<{ userId: string }>;
      if (event.detail.userId !== user?.uid) return;
      console.log('[AppContext] email-verification-completed received — updating state');
      const ts = Date.now();
      setEmailVerificationCompletedAt(ts);
      // Trigger reconciliation if pending preview exists
      if (!hasAttemptedReconciliationRef.current && !previewActive) {
        hasAttemptedReconciliationRef.current = true;
        try {
          const result = await activatePreviewAfterVerification();
          console.log('[PREVIEW_RECONCILE] email-verified trigger result:', result.status);
          if (result.status === 'activated' || result.status === 'already_active') {
            applyEntitlement(result);
          }
        } catch (err) {
          console.warn('[PREVIEW_RECONCILE] failed (non-fatal):', err);
        }
      }
    }
    window.addEventListener('email-verification-completed', handleEmailVerificationCompleted);
    return () => window.removeEventListener('email-verification-completed', handleEmailVerificationCompleted);
  }, [user?.uid, previewActive]);

  // Listen for premium-preview-pending-written event (fired by OnboardingScreen after storing intent)
  useEffect(() => {
    async function handlePreviewPendingWritten(evt: Event) {
      const event = evt as CustomEvent<{ userId: string }>;
      if (event.detail.userId !== user?.uid) return;
      if (hasAttemptedReconciliationRef.current) return;
      if (previewActive) return;
      console.log('[AppContext] Received premium-preview-pending-written — running reconciliation');
      hasAttemptedReconciliationRef.current = true;
      try {
        const result = await activatePreviewAfterVerification();
        console.log('[PREVIEW_RECONCILE] backend result:', result.status);
        if (result.status === 'activated' || result.status === 'already_active') {
          applyEntitlement(result);
        }
      } catch (err) {
        console.warn('[PREVIEW_RECONCILE] failed (non-fatal):', err);
      }
    }
    window.addEventListener('premium-preview-pending-written', handlePreviewPendingWritten);
    return () => window.removeEventListener('premium-preview-pending-written', handlePreviewPendingWritten);
  }, [user?.uid, previewActive]);

  // Weekly Coaching
  const [weeklyCoaching, setWeeklyCoaching] =
    useState<WeeklyCoachingResult | null>(null);
  const [weeklyCoachingLoading, setWeeklyCoachingLoading] = useState(true);

  useEffect(() => {
    // Demo mode: provide seeded coaching bullets
    if (isDemoMode) {
      setWeeklyCoaching({
        exercise: [
          '4 gym sessions this week — solid consistency. Push/pull split is well balanced.',
          'Evening walks adding 150-180 cal daily burn. Consider adding a weekend run to hit cardio targets.',
        ],
        nutrition_l2: [
          'Protein averaging 153g/day — close to your 160g minimum. Add a Greek yoghurt snack to close the gap.',
          'Fibre is the weak spot at 16g vs 35g target. Add beans, lentils, or a high-fibre cereal.',
          'Hydration at 2.1L vs 3.15L target — set hourly water reminders.',
        ],
        body_stats: [
          'Weight trending down 2.8kg over 2 weeks — healthy rate for GLP-1.',
          'Body fat down 1.3% while muscle mass up 0.6kg — recomp going well.',
          'BP 122/78 and RHR 68 — both in healthy range.',
        ],
        behaviour: [
          'Logging streak at 23 days — consistency is your superpower.',
          'Morning gym routine is locked in. Keep the 06:45 slot.',
        ],
      });
      setWeeklyCoachingLoading(false);
      return;
    }

    const userId = user?.uid;
    if (!userId) {
      setWeeklyCoaching(null);
      setWeeklyCoachingLoading(false);
      return;
    }

    // Wait for profile to load so we can check notifications_enabled
    if (!profileLoaded) return;

    // Weekly coaching is a premium feature — do not fire for basic users
    const entitled = isAdmin || previewActive || plan === 'premium' || plan === 'trial';
    if (!entitled) {
      setWeeklyCoaching(null);
      setWeeklyCoachingLoading(false);
      return;
    }

    let mounted = true;

    async function loadCoaching() {
      try {
        // Coaching service needs its own profile fetch for the evaluator logic
        let profile = null;
        try {
          profile = await getUserProfile(userId!);
        } catch (profileError) {
          console.warn("[AppContext] getUserProfile failed (new user?) — continuing without profile:", profileError);
        }

        if (profile?.notifications_enabled === false) {
          if (mounted) {
            setWeeklyCoaching(null);
            setWeeklyCoachingLoading(false);
          }
          return;
        }
        const cached = await getWeeklyCoaching(userId!);
        if (!cached && shouldRunWeeklyEvaluator()) {
          const generated = await generateAndCacheWeeklyCoaching(userId!);
          if (mounted) {
            setWeeklyCoaching(generated);
            setWeeklyCoachingLoading(false);
          }
        } else {
          if (mounted) {
            setWeeklyCoaching(cached);
            setWeeklyCoachingLoading(false);
          }
        }
      } catch (error) {
        console.error("[AppContext] Failed to load weekly coaching:", error);
        if (mounted) {
          setWeeklyCoaching(null);
          setWeeklyCoachingLoading(false);
        }
      }
    }

    loadCoaching();

    const interval = setInterval(
      () => {
        if (shouldRunWeeklyEvaluator()) loadCoaching();
      },
      60 * 60 * 1000,
    );

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [user?.uid, isDemoMode, profileLoaded, isAdmin, previewActive, plan]);

  // Single instance of useDailySummary — prevents duplicate Firestore fetches
  // when multiple screens mount simultaneously.
  // Pass uid as a primitive string (not the user object) so AppContext re-renders
  // don't recreate refreshDailySummary via new object reference.
  // Gate behind onboarding completion — new users must not trigger data reads.
  const dailySummary = useDailySummary(
    isDemoMode ? null : (profileLoaded && onboardingComplete ? (user?.uid ?? null) : null),
    isDemoMode,
  );

  const refetchSubscription = async (): Promise<void> => {
    if (isDemoMode || !user) return;
    try {
      const res = await getSubscription();
      const p = res.data?.plan;
      if (p === 'premium' || p === 'trial') setPlan(p);
      else setPlan('basic');
      setPremiumPreviewExpiresAt(res.data?.premiumPreviewExpiresAt ?? null);
      setPremiumPreviewUsed(res.data?.premiumPreviewUsed ?? false);
      setPreviewActive(res.data?.previewActive ?? false);
      setEntitlementReason((res.data?.entitlementReason as AppEntitlementReason) ?? 'none');
    } catch {
      // Non-fatal — do not clobber existing entitlement state on error
    }
  };

  const refreshAuthUser = async (): Promise<void> => {
    console.log('[VERIFY] refreshAuthUser clicked');

    const current = auth.currentUser;
    if (!current) {
      console.error('[VERIFY] no current user');
      return;
    }

    console.log('[VERIFY] before reload', current.emailVerified);

    try {
      await current.reload();
      await current.getIdToken(true);
    } catch (err) {
      console.error('[VERIFY] reload failed', err);
      return;
    }

    const fresh = auth.currentUser;
    console.log('[VERIFY] after reload', fresh?.emailVerified);

    if (!fresh) {
      console.error('[VERIFY] current user missing after reload');
      return;
    }

    setUser(prev => ({
      uid: fresh.uid,
      email: fresh.email,
      displayName: fresh.displayName,
      emailVerified: prev?.emailVerified === true ? true : fresh.emailVerified,
    }));

    // Refetch subscription to hydrate entitlement — activation handled by applyEntitlement
    await refetchSubscription();
  };

  // Ref to guard once-per-session passive reconciliation
  const hasAttemptedReconciliationRef = useRef(false);

  // Single writer for all entitlement state — call this with backend payload
  const applyEntitlement = (payload: import('../services/premiumPreview.service').ActivateAfterVerificationResponse): void => {
    if (payload.isPremium !== undefined) {
      const p = payload.plan;
      if (p === 'premium' || p === 'trial') setPlan(p);
      else setPlan('basic');
    }
    if (payload.previewActive !== undefined) setPreviewActive(payload.previewActive);
    if (payload.premiumPreviewUsed !== undefined) setPremiumPreviewUsed(payload.premiumPreviewUsed);
    if (payload.premiumPreviewExpiresAt !== undefined) setPremiumPreviewExpiresAt(payload.premiumPreviewExpiresAt ?? null);
    if (payload.entitlementReason !== undefined) setEntitlementReason(payload.entitlementReason as AppEntitlementReason);
  };

  const value: AppContextValue = {
    user: isDemoMode ? null : user,
    loading: isDemoMode ? false : loading,
    isDemoMode,
    authError: isDemoMode ? null : authError,
    userCreationTime: isDemoMode ? null : userCreationTime,
    weeklyCoaching,
    weeklyCoachingLoading,
    glp1Enabled,
    glp1DoseDay,
    glp1CheckComplete,
    setGlp1Enabled,
    setGlp1DoseDay,
    setGlp1EnabledAndPersist,
    setGlp1DoseDayAndPersist,
    // Profile-based flags
    profileSettled: isDemoMode ? true : profileLoaded,
    profileError: isDemoMode ? null : profileError,
    onboardingComplete: isDemoMode ? true : onboardingComplete,
    onboardingStatus: isDemoMode
      ? 'complete'
      : !profileLoaded
        ? 'loading'
        : profileError
          ? 'error'
          : onboardingComplete
            ? 'complete'
            : 'incomplete',
    isAdmin: isDemoMode ? false : isAdmin,
    firstName: isDemoMode ? null : firstName,
    // Consolidated readiness flag — see AppContextValue.isAppReady JSDoc.
    // Derived from the most stable existing context state; no new source of truth.
    // Demo mode bypasses all checks (no auth, no profile fetch).
    isAppReady: isDemoMode
      ? true
      : !loading
        && user !== null
        && authError === null
        && profileLoaded
        && profileError === null,
    retryProfile,
    dailySummary,
    // Feature gating
    isPremium: isDemoMode ? true : isAdmin ? true : plan === 'premium' || plan === 'trial' || previewActive,
    plan: isDemoMode ? 'premium' : isAdmin ? 'premium' : plan,
    subscriptionLoading,
    entitlementReason,
    premiumPreviewExpiresAt,
    premiumPreviewUsed,
    previewActive,
    emailVerificationCompletedAt,
    refetchSubscription,
    refreshAuthUser,
    applyEntitlement,
    functionalAgeHasEverComputed: isDemoMode ? false : functionalAgeHasEverComputed,
    // Starter modal central store
    starterModalsLoaded: isDemoMode ? true : starterModalsLoaded,
    starterModalsSeen: isDemoMode
      ? { home: true, nutrition: true, exercise: true, body_stats: true, functional_age: true, glp1_hub: true, a2hs: true }
      : starterModalsSeen,
    markStarterSeen,
  };

  useEffect(() => {
    if (isDemoMode) {
      console.log("[AppContext] [ITEM 4] Demo mode context value:", {
        isDemoMode: true,
        user: null,
        loading: false,
        authError: null,
        note: "All auth checks bypassed, demo data will be used",
      });
    }
  }, [isDemoMode]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) {
    throw new Error("useAppContext must be used within AppProvider");
  }
  return ctx;
}
